// PREFERENCIAS DE PRESENTACIÓN DEL RADAR — locales, versionadas y aisladas por USUARIO + SITIO.
// Sólo guarda IDs autorizados y opciones de presentación: nunca nombres, casos, credenciales ni
// snapshots de residentes. Si el almacenamiento local no está disponible (modo privado, cuota,
// iframe sin permisos), el Radar sigue funcionando con los valores por defecto en memoria.
import { useCallback, useEffect, useMemo, useState } from "react";

const VERSION = 1;
const PREFIJO = "galarya.radar";

export const PREFS_DEFAULT = {
  v: VERSION,
  cantidad: 8,          // habitaciones VISIBLES (no columnas) · sólo institucional
  orden: "numero",      // institucional: numero | estado · residencial: prioridad | nombre | sitio
  densidad: "normal",   // institucional: normal | compacta
  modo: "todas",        // residencial: todas | mi_vista | con_caso
  miVista: [],          // IDs elegidos por esta persona para ESTE ámbito (zonas o personas)
  usarMiVista: false,
};

const clave = (ambito, userKey, scopeId) =>
  `${PREFIJO}.${ambito}.${VERSION}.${userKey || "anon"}.${scopeId || "-"}`;

function leer(k) {
  try {
    const crudo = window.localStorage.getItem(k);
    if (!crudo) return null;
    const p = JSON.parse(crudo);
    return p && p.v === VERSION ? p : null;
  } catch { return null; }
}

function escribir(k, valor) {
  try { window.localStorage.setItem(k, JSON.stringify(valor)); return true; }
  catch { return false; }
}

/**
 * @param userKey identificador de la sesión (id o email): aísla la selección de Laura de la de Carmen.
 * @param scopeId ámbito de la preferencia (sitio en institucional, «residencial» en la cartera).
 * @param idsAutorizados ids que el backend acaba de autorizar (zonas o personas); los guardados que
 *   ya no aparecen se descartan en la lectura (no se "resucita" nada retirado).
 * @param ambito prefijo de la clave: separa las preferencias institucionales de las residenciales.
 */
export default function useRadarPrefs(userKey, scopeId, idsAutorizados, ambito = "inst") {
  const k = clave(ambito, userKey, scopeId);
  const [prefs, setPrefs] = useState(PREFS_DEFAULT);
  const [persistente, setPersistente] = useState(true);

  useEffect(() => {
    const guardado = leer(k);
    setPrefs(guardado ? { ...PREFS_DEFAULT, ...guardado } : PREFS_DEFAULT);
  }, [k]);

  const autorizados = useMemo(() => new Set(idsAutorizados || []), [idsAutorizados]);
  // VALIDACIÓN contra la respuesta autorizada VIGENTE: un id guardado que hoy no está autorizado
  // no se usa (y tampoco se borra del almacenamiento: la autorización puede volver).
  const miVista = useMemo(
    () => (prefs.miVista || []).filter((id) => autorizados.size === 0 || autorizados.has(id)),
    [prefs.miVista, autorizados],
  );

  const set = useCallback((parcial) => {
    setPrefs((prev) => {
      const siguiente = { ...prev, ...parcial, v: VERSION };
      setPersistente(escribir(k, siguiente));
      return siguiente;
    });
  }, [k]);

  const alternarHabitacion = useCallback((zonaId) => {
    setPrefs((prev) => {
      const actual = prev.miVista || [];
      const lista = actual.includes(zonaId) ? actual.filter((x) => x !== zonaId) : [...actual, zonaId];
      const siguiente = { ...prev, miVista: lista, v: VERSION };
      setPersistente(escribir(k, siguiente));
      return siguiente;
    });
  }, [k]);

  return { prefs: { ...prefs, miVista }, set, alternarHabitacion,
           alternarId: alternarHabitacion, persistente };
}
